import React, { useMemo, useState } from 'react';
import { CalendarRange, ChevronRight, Flag } from 'lucide-react';

// Roadmap sur téléphone : la frise (itérations côte à côte dans le temps) est illisible sur un écran étroit ; on
// montre la liste des itérations, celle en cours mise en avant, avec l'avancement dans le temps et la vision.

const DAY = 24 * 60 * 60 * 1000;
const toDate = (value) => (value ? new Date(`${String(value).slice(0, 10)}T12:00:00`) : null);
const shortDate = (date) => (date ? date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit' }) : '…');

const MobileRoadmap = ({ iterations = [], milestones = [], onIterationSelect }) => {
  const today = useMemo(() => new Date(), []);
  const [showPast, setShowPast] = useState(false);
  const items = useMemo(() => [...iterations]
    .map((iteration) => {
      const start = toDate(iteration.start_date);
      const end = toDate(iteration.end_date) || start;
      const state = !start ? 'future' : end && end < today ? 'past' : start > today ? 'future' : 'current';
      const elapsed = state === 'current' && start && end && end > start
        ? Math.min(100, Math.max(0, Math.round(((today - start) / (end - start)) * 100))) : state === 'past' ? 100 : 0;
      const daysLeft = state === 'current' && end ? Math.max(0, Math.ceil((end - today) / DAY)) : null;
      return { iteration, start, end, state, elapsed, daysLeft };
    })
    .sort((a, b) => (a.start?.getTime() || 0) - (b.start?.getTime() || 0)), [iterations, today]);

  const upcomingMilestones = useMemo(() => milestones
    .map((m) => ({ ...m, when: toDate(m.date || m.due_date || m.target_date) }))
    .filter((m) => m.when && m.when >= new Date(today.getTime() - DAY))
    .sort((a, b) => a.when - b.when)
    .slice(0, 3), [milestones, today]);

  // Itérations passées repliées ; s'il n'y a ni en cours ni à venir, la dernière reste visible.
  const lastId = items.length && items.every((item) => item.state === 'past') ? items[items.length - 1].iteration.id : null;
  const pastCount = items.filter((item) => item.state === 'past' && item.iteration.id !== lastId).length;
  if (!items.length) {
    return <p className="rounded-xl bg-slate-50 px-3 py-4 text-sm text-slate-500">Aucune itération pour l’instant.</p>;
  }
  return (
    <div className="space-y-2.5">
      {upcomingMilestones.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {upcomingMilestones.map((m) => (
            <span key={m.id} className="flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-700">
              <Flag className="h-3 w-3" /> {m.name || m.title} · {shortDate(m.when)}
            </span>
          ))}
        </div>
      )}
      {pastCount > 0 && (
        <button type="button" onClick={() => setShowPast((value) => !value)}
          className="w-full rounded-lg px-2 py-1 text-left text-xs font-medium text-slate-500 hover:bg-slate-50">
          {showPast ? 'Masquer' : 'Voir'} {pastCount} itération{pastCount > 1 ? 's' : ''} passée{pastCount > 1 ? 's' : ''}
        </button>
      )}
      {items.filter((item) => showPast || item.state !== 'past' || item.iteration.id === lastId).map(({ iteration, start, end, state, elapsed, daysLeft }) => (
        <button
          key={iteration.id}
          type="button"
          onClick={() => onIterationSelect?.(iteration)}
          className={`flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition ${
            state === 'current' ? 'border-blue-200 bg-blue-50/70 shadow-sm' : 'border-slate-200 bg-white'
          } ${state === 'past' ? 'opacity-60' : ''}`}
        >
          <CalendarRange className={`mt-0.5 h-4 w-4 shrink-0 ${state === 'current' ? 'text-blue-600' : 'text-slate-400'}`} />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="truncate text-sm font-semibold text-slate-900">{iteration.name || `Itération ${iteration.id}`}</span>
              {state === 'current' && (
                <span className="shrink-0 rounded-full bg-blue-600 px-2 py-px text-[10px] font-semibold text-white">En cours</span>
              )}
            </span>
            <span className="block text-xs text-slate-500">
              {shortDate(start)} → {shortDate(end)}{daysLeft !== null ? ` · encore ${daysLeft} j` : ''}
              {Array.isArray(iteration.sprints) && iteration.sprints.length > 0 ? ` · ${iteration.sprints.length} sprint${iteration.sprints.length > 1 ? 's' : ''}` : ''}
            </span>
            {state === 'current' && (
              <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-blue-100">
                <span className="block h-full rounded-full bg-blue-500" style={{ width: `${elapsed}%` }} />
              </span>
            )}
            {iteration.description && (
              <span className="mt-1 line-clamp-2 block text-xs text-slate-600">{iteration.description}</span>
            )}
          </span>
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
        </button>
      ))}
    </div>
  );
};

export default MobileRoadmap;
